import { useState, useMemo } from 'react'
import {
  useClaimWorkflowMappings,
  useCreateClaimWorkflowMapping,
  useDeleteClaimWorkflowMapping,
  type ClaimWorkflowMapping,
  type ClaimMappingLevel,
} from '@/admin/hooks/use-claim-mappings'
import { useClaimWorkflows } from '@/admin/hooks/use-claim-workflows'
import { useProducts, useProductTemplates, useProductCategories } from '@/admin/hooks/use-products'
import { useWarrantyTemplates } from '@/admin/hooks/use-warranties'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/shared/components/ui/select'
import { ConfirmationDialog } from '@/admin/components/shared/confirmation-dialog'
import { Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

// ── Constants ───────────────────────────────────────────

import { toastApiError } from '@/shared/lib/api-error'
const LEVEL_ORDER: ClaimMappingLevel[] = ['GLOBAL', 'TEMPLATE', 'CATEGORY', 'PRODUCT', 'WARRANTY_TEMPLATE']

const LEVEL_COLOURS: Record<ClaimMappingLevel, string> = {
  GLOBAL: 'bg-[var(--scope-purple-bg)] text-[var(--ink-2)]',
  TEMPLATE: 'bg-[var(--scope-blue-bg)] text-[var(--status-info)]',
  CATEGORY: 'bg-[var(--bg-subtle)] text-[var(--ink-2)]',
  PRODUCT: 'bg-[var(--green-bg-muted)] text-[var(--green)]',
  WARRANTY_TEMPLATE: 'bg-[var(--scope-orange-bg)] text-[var(--ink-2)]',
}

// ── Helpers ─────────────────────────────────────────────

function targetName(m: ClaimWorkflowMapping): string {
  switch (m.level) {
    case 'PRODUCT':
      return m.product_name ?? 'Unknown product'
    case 'CATEGORY':
      return m.category_name ?? 'Unknown category'
    case 'TEMPLATE':
      return m.product_template_name ?? 'Unknown template'
    case 'WARRANTY_TEMPLATE':
      return m.warranty_template_name ?? 'Unknown warranty template'
    case 'GLOBAL':
      return 'Organisation-wide'
  }
}

function levelLabel(level: ClaimMappingLevel): string {
  return level === 'WARRANTY_TEMPLATE'
    ? 'Warranty template'
    : level.charAt(0) + level.slice(1).toLowerCase()
}

// ── Local Components ────────────────────────────────────

function GreenButton({ children, onClick, 'data-testid': dataTestId }: { children: React.ReactNode; onClick: () => void; 'data-testid'?: string }) {
  return (
    <button
      onClick={onClick}
      data-testid={dataTestId}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-[4px] text-[11px] uppercase tracking-[0.1em] transition-all duration-150 cursor-pointer bg-[var(--green)] hover:bg-[var(--green-hover)] text-[var(--bg)]"
    >
      {children}
    </button>
  )
}

function IconButton({
  children,
  onClick,
  danger,
  'data-testid': dataTestId,
}: {
  children: React.ReactNode
  onClick: () => void
  danger?: boolean
  'data-testid'?: string
}) {
  return (
    <button
      onClick={onClick}
      data-testid={dataTestId}
      className={`h-7 w-7 flex items-center justify-center rounded-[4px] transition-all duration-100 cursor-pointer text-[var(--ink-3)] ${
        danger
          ? 'hover:bg-[var(--status-error-bg-subtle)] hover:text-[var(--status-error)]'
          : 'hover:bg-[var(--bg-subtle)]'
      }`}
    >
      {children}
    </button>
  )
}

function LevelBadge({ level }: { level: ClaimMappingLevel }) {
  const s = LEVEL_COLOURS[level]
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.08em]${s}`}
    >
      {level === 'WARRANTY_TEMPLATE' ? 'warranty template' : level.toLowerCase()}
    </span>
  );
}

// ── Flatten categories helper ───────────────────────────

function flattenCats(
  cats: Array<{ uuid: string; name: string; children?: unknown[] }> | undefined,
  depth = 0,
): Array<{ uuid: string; name: string; depth: number }> {
  if (!cats) return []
  const result: Array<{ uuid: string; name: string; depth: number }> = []
  for (const cat of cats) {
    result.push({ uuid: cat.uuid, name: cat.name, depth })
    if (cat.children && Array.isArray(cat.children) && cat.children.length) {
      result.push(...flattenCats(cat.children as Array<{ uuid: string; name: string; children?: unknown[] }>, depth + 1))
    }
  }
  return result
}

// ── Target Selector ─────────────────────────────────────

function TargetSelector({
  level,
  productUuid,
  setProductUuid,
  categoryUuid,
  setCategoryUuid,
  templateUuid,
  setTemplateUuid,
  warrantyTemplateUuid,
  setWarrantyTemplateUuid,
}: {
  level: ClaimMappingLevel
  productUuid: string | null
  setProductUuid: (v: string | null) => void
  categoryUuid: string | null
  setCategoryUuid: (v: string | null) => void
  templateUuid: string | null
  setTemplateUuid: (v: string | null) => void
  warrantyTemplateUuid: string | null
  setWarrantyTemplateUuid: (v: string | null) => void
}) {
  const [productSearch, setProductSearch] = useState('')
  const { data: productsData } = useProducts(
    level === 'PRODUCT' ? { search: productSearch, page: '1' } : undefined,
  )
  const products = productsData?.results ?? []
  const { data: templates } = useProductTemplates()
  const { data: categories } = useProductCategories()
  const { data: warrantyTemplates } = useWarrantyTemplates()
  const flatCats = flattenCats(categories)

  if (level === 'GLOBAL') return null

  if (level === 'PRODUCT') {
    return (
      <div>
        <Label className="clara-section-label mb-1.5 block">Product</Label>
        <Input
          placeholder="Search products..."
          value={productSearch}
          onChange={(e) => setProductSearch(e.target.value)}
          className="text-[13px] mb-1.5"
        />
        {products.length > 0 && (
          <div
            className="max-h-32 overflow-y-auto rounded-[4px] border-[0.5px] border-[var(--rule)]"
          >
            {products.map((p) => (
                <button
                  key={p.uuid}
                  onClick={() => setProductUuid(p.uuid)}
                  className={`w-full cursor-pointer px-3 py-1.5 text-left text-[12px] transition-all duration-100 ${
                    productUuid === p.uuid
                      ? 'bg-[var(--green-bg-subtle)] text-[var(--green)]'
                      : 'text-[var(--ink-2)] hover:bg-[var(--bg-subtle)]'
                  }`}
                >
                {p.cached_long_name || p.name}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (level === 'CATEGORY') {
    return (
      <div>
        <Label className="clara-section-label mb-1.5 block">Category</Label>
        <Select
          value={categoryUuid ?? ''}
          onValueChange={(v) => setCategoryUuid(v || null)}
        >
          <SelectTrigger size="sm" className="text-[13px] w-full">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            {flatCats.map((c) => (
              <SelectItem key={c.uuid} value={c.uuid}>
                {'  '.repeat(c.depth)}{c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (level === 'TEMPLATE') {
    return (
      <div>
        <Label className="clara-section-label mb-1.5 block">Product Template</Label>
        <Select
          value={templateUuid ?? ''}
          onValueChange={(v) => setTemplateUuid(v || null)}
        >
          <SelectTrigger size="sm" className="text-[13px] w-full">
            <SelectValue placeholder="Select template" />
          </SelectTrigger>
          <SelectContent>
            {templates?.map((tmpl) => (
              <SelectItem key={tmpl.uuid} value={tmpl.uuid}>
                {tmpl.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (level === 'WARRANTY_TEMPLATE') {
    return (
      <div>
        <Label className="clara-section-label mb-1.5 block">Warranty Template</Label>
        <Select
          value={warrantyTemplateUuid ?? ''}
          onValueChange={(v) => setWarrantyTemplateUuid(v || null)}
        >
          <SelectTrigger size="sm" className="text-[13px] w-full">
            <SelectValue placeholder="Select warranty template" />
          </SelectTrigger>
          <SelectContent>
            {warrantyTemplates?.map((wc) => (
              <SelectItem key={wc.uuid} value={wc.uuid}>
                {wc.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  return null
}

// ── Add Workflow Mapping Dialog ──────────────────────────

function AddWorkflowMappingDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { data: workflows } = useClaimWorkflows()
  const createMutation = useCreateClaimWorkflowMapping()

  const [level, setLevel] = useState<ClaimMappingLevel>('GLOBAL')
  const [productUuid, setProductUuid] = useState<string | null>(null)
  const [categoryUuid, setCategoryUuid] = useState<string | null>(null)
  const [templateUuid, setTemplateUuid] = useState<string | null>(null)
  const [warrantyTemplateUuid, setWarrantyTemplateUuid] = useState<string | null>(null)
  const [workflowUuid, setWorkflowUuid] = useState('')
  const [priority, setPriority] = useState(0)

  function reset() {
    setLevel('GLOBAL')
    setProductUuid(null)
    setCategoryUuid(null)
    setTemplateUuid(null)
    setWarrantyTemplateUuid(null)
    setWorkflowUuid('')
    setPriority(0)
  }

  async function handleSubmit() {
    if (!workflowUuid) {
      toast.error('Please select a claim workflow')
      return
    }
    try {
      await createMutation.mutateAsync({
        level,
        claim_workflow_uuid: workflowUuid,
        priority,
        ...(level === 'PRODUCT' && productUuid ? { product_uuid: productUuid } : {}),
        ...(level === 'CATEGORY' && categoryUuid ? { category_uuid: categoryUuid } : {}),
        ...(level === 'TEMPLATE' && templateUuid ? { product_template_uuid: templateUuid } : {}),
        ...(level === 'WARRANTY_TEMPLATE' && warrantyTemplateUuid ? { warranty_template_uuid: warrantyTemplateUuid } : {}),
      })
      toast.success('Workflow mapping created')
      reset()
      onOpenChange(false)
    } catch (err) {
      toastApiError(err, 'Failed to create workflow mapping');
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v) }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Workflow Mapping</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          {/* Level */}
          <div>
            <Label className="clara-section-label mb-1.5 block">Level</Label>
            <Select
              value={level}
              onValueChange={(v) => {
                setLevel(v as ClaimMappingLevel)
                setProductUuid(null)
                setCategoryUuid(null)
                setTemplateUuid(null)
                setWarrantyTemplateUuid(null)
              }}
            >
              <SelectTrigger size="sm" className="text-[13px] w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LEVEL_ORDER.map((l) => (
                  <SelectItem key={l} value={l}>
                    {levelLabel(l)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Target */}
          <TargetSelector
            level={level}
            productUuid={productUuid}
            setProductUuid={setProductUuid}
            categoryUuid={categoryUuid}
            setCategoryUuid={setCategoryUuid}
            templateUuid={templateUuid}
            setTemplateUuid={setTemplateUuid}
            warrantyTemplateUuid={warrantyTemplateUuid}
            setWarrantyTemplateUuid={setWarrantyTemplateUuid}
          />

          {/* Workflow */}
          <div>
            <Label className="clara-section-label mb-1.5 block">Claim Workflow</Label>
            <Select value={workflowUuid} onValueChange={setWorkflowUuid}>
              <SelectTrigger size="sm" className="text-[13px] w-full">
                <SelectValue placeholder="Select workflow" />
              </SelectTrigger>
              <SelectContent>
                {workflows?.map((w) => (
                  <SelectItem key={w.uuid} value={w.uuid}>
                    {w.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Priority */}
          <div>
            <Label className="clara-section-label mb-1.5 block">Priority</Label>
            <Input
              type="number"
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value) || 0)}
              className="text-[13px] w-24"
              min={0}
            />
            <p className="mt-1 text-[10px] text-[var(--ink-3)]">
              Higher priority mappings take precedence
            </p>
          </div>
        </div>

        <DialogFooter>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { reset(); onOpenChange(false) }}
              className="cursor-pointer rounded-[4px] border-[0.5px] border-[var(--rule-strong)] px-4 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={createMutation.isPending}
              data-testid="claim-mappings-dialog-submit"
              className="px-4 py-1.5 rounded-[4px] text-[11px] uppercase tracking-[0.1em] transition-all duration-150 cursor-pointer disabled:opacity-40 bg-[var(--green)] hover:bg-[var(--green-hover)] text-[var(--bg)]"
            >
              Create
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Page Component ──────────────────────────────────────

export function ClaimMappingsPage() {
  const { data: workflowMappings, isLoading } = useClaimWorkflowMappings()
  const deleteWorkflow = useDeleteClaimWorkflowMapping()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [deleting, setDeleting] = useState<ClaimWorkflowMapping | null>(null)

  const grouped = useMemo(() => {
    if (!workflowMappings) return []
    return LEVEL_ORDER
      .map((level) => ({
        level,
        items: workflowMappings.filter((m) => m.level === level),
      }))
      .filter((g) => g.items.length > 0)
  }, [workflowMappings])

  async function handleDelete() {
    if (!deleting) return
    try {
      await deleteWorkflow.mutateAsync(deleting.uuid)
      toast.success('Workflow mapping deleted')
      setDeleting(null)
    } catch (err) {
      toastApiError(err, 'Failed to delete workflow mapping');
    }
  }

  return (
    <div>
      {/* Page Header */}
      <div className="mb-8">
        <h2 className="clara-heading text-[24px]">Claim Mappings</h2>
        <p className="mt-0.5 text-[12px] text-[var(--ink-3)]">
          Map claim workflows to products, categories, templates, or warranty templates
        </p>
      </div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="clara-heading text-[18px]">Workflow Mappings</h3>
          <p className="mt-0.5 text-[11px] text-[var(--ink-3)]">
            Which claim workflow is used when a claim is filed against a product
          </p>
        </div>
        <GreenButton onClick={() => setDialogOpen(true)} data-testid="claim-mappings-add-btn">
          <Plus className="h-3.5 w-3.5" />
          Add Mapping
        </GreenButton>
      </div>
      {isLoading ? (
        <p className="py-10 text-center text-[13px] text-[var(--ink-3)]">
          Loading...
        </p>
      ) : !workflowMappings?.length ? (
        <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] py-12 text-center">
          <p className="text-[13px] text-[var(--ink-3)]">
            No workflow mappings yet
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {grouped.map(({ level, items }) => (
            <div key={level}>
              <div className="flex items-center gap-2 mb-2">
                <LevelBadge level={level} />
                <span className="text-[11px] uppercase tracking-[0.08em] text-[var(--ink-3)]">
                  {items.length} {items.length === 1 ? 'mapping' : 'mappings'}
                </span>
              </div>

              <div className="flex flex-col gap-1.5">
                {items.map((m) => (
                  <div
                    key={m.uuid}
                    className="flex items-center justify-between gap-4 rounded-[8px] border-[0.5px] border-[var(--rule)] px-4 py-3 transition-all duration-100 hover:bg-[var(--bg-subtle)]"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-[var(--ink)]">
                        {targetName(m)}
                      </p>
                      <p className="mt-0.5 text-[11px] text-[var(--ink-3)]">
                        Workflow: {m.claim_workflow_name}
                        {m.priority > 0 && <span className="ml-3">Priority: {m.priority}</span>}
                      </p>
                    </div>
                    <IconButton onClick={() => setDeleting(m)} danger data-testid={`claim-mappings-delete-${m.uuid}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </IconButton>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <AddWorkflowMappingDialog open={dialogOpen} onOpenChange={setDialogOpen} />
      <ConfirmationDialog
        open={!!deleting}
        onOpenChange={(next) => {
          if (!next) setDeleting(null)
        }}
        title={`Delete workflow mapping for ${deleting ? targetName(deleting) : 'target'}?`}
        description="This removes the workflow selection for matching future claims."
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        destructive
        isPending={deleteWorkflow.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
}
